import React from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "../../../hooks"
import "./daily-quests.css"

interface QuestMilestone {
  games: number
  boosters: number
}

const DAILY_MILESTONES: QuestMilestone[] = [
  { games: 1, boosters: 1 },
  { games: 3, boosters: 1 }
]

const WEEKLY_MILESTONES: QuestMilestone[] = [
  { games: 10, boosters: 3 },
  { games: 20, boosters: 3 }
]

function QuestSection({
  label,
  current,
  max,
  milestones,
  rewarded
}: {
  label: string
  current: number
  max: number
  milestones: QuestMilestone[]
  rewarded: number
}) {
  const { t } = useTranslation()
  const progress = Math.min(current, max)
  const pct = (progress / max) * 100

  return (
    <div className="quest-section">
      <div className="quest-section-header">
        <span className="quest-section-label">{label}</span>
        <span className="quest-section-count">
          {t("quests.progress", { current: Math.min(current, max), max })}
        </span>
      </div>

      <div className="quest-track">
        <div className="quest-bar-bg">
          <div className="quest-bar-fill" style={{ width: `${pct}%` }} />
        </div>

        {milestones.map((m, i) => {
          const bit = 1 << i
          const claimed = !!(rewarded & bit)
          const reached = current >= m.games
          const milestoneLeft = `${(m.games / max) * 100}%`

          return (
            <div
              key={m.games}
              className={`quest-milestone ${claimed ? "claimed" : reached ? "reached" : "locked"}`}
              style={{ left: milestoneLeft }}
              title={t("quests.milestone_tooltip", {
                games: m.games,
                boosters: m.boosters
              })}
            >
              <div className="quest-milestone-dot" />
              <div className="quest-milestone-reward">
                <img src="/assets/ui/booster.png" alt="" />
                <span>×{m.boosters}</span>
              </div>
              {claimed && <span className="quest-milestone-check">✓</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Returns "YYYY-MM-DD" in Beijing time (UTC+8) */
function getBJDayString(date: Date): string {
  const bj = new Date(date.getTime() + 8 * 60 * 60 * 1000)
  return bj.toISOString().slice(0, 10)
}

/** Returns "YYYY-MM-DD" of Monday in Beijing time (UTC+8) */
function getBJMondayString(date: Date): string {
  const bj = new Date(date.getTime() + 8 * 60 * 60 * 1000)
  const day = bj.getUTCDay()
  bj.setUTCDate(bj.getUTCDate() - (day === 0 ? 6 : day - 1))
  return bj.toISOString().slice(0, 10)
}

export function DailyQuests() {
  const { t } = useTranslation()
  const profile = useAppSelector((state) => state.network.profile)

  if (!profile) return null

  const now = new Date()
  const todayBJ = getBJDayString(now)
  const weekMondayBJ = getBJMondayString(now)

  // If server hasn't had a chance to reset yet (no qualifying game played today/this week),
  // show 0 client-side so the display is always accurate
  const isNewDay =
    !profile.dailyQuestLastReset ||
    getBJDayString(new Date(profile.dailyQuestLastReset)) !== todayBJ
  const isNewWeek =
    !profile.weeklyQuestLastReset ||
    getBJMondayString(new Date(profile.weeklyQuestLastReset)) !== weekMondayBJ

  const dailyGames = isNewDay ? 0 : (profile.dailyQuestGames ?? 0)
  const dailyRewarded = isNewDay ? 0 : (profile.dailyQuestRewarded ?? 0)
  const weeklyGames = isNewWeek ? 0 : (profile.weeklyQuestGames ?? 0)
  const weeklyRewarded = isNewWeek ? 0 : (profile.weeklyQuestRewarded ?? 0)

  return (
    <div className="daily-quests my-container custom-bg hidden-scrollable">
      <h2>{t("quests.title")}</h2>
      <p className="quest-hint">{t("quests.hint")}</p>

      <QuestSection
        label={t("quests.daily_label")}
        current={dailyGames}
        max={3}
        milestones={DAILY_MILESTONES}
        rewarded={dailyRewarded}
      />

      <QuestSection
        label={t("quests.weekly_label")}
        current={weeklyGames}
        max={20}
        milestones={WEEKLY_MILESTONES}
        rewarded={weeklyRewarded}
      />
    </div>
  )
}
